/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


class TermsModal {
    constructor({ terms: terms, termsCheck: termsCheck }) {
        this.main = terms;
        this.checkBox = termsCheck;
        this.pageContent = terms.querySelector('.terms__page-content-inner');

        this.Click = this.Click.bind(this);

        this.Init();
    }

    Init() {
        this.main.classList.add('init');
        this.Open();

        this.FullTermsText();

        this.Eventlisteners();
    }

    Eventlisteners() {
        document.addEventListener(
            'terms:modal:open',
            () => {
                this.Open();
            },
            true
        );
    }

    Click(event) {
        if (event.target.classList.contains('terms-accept')) {
            termsCheck.checked = true;

            const submitButton = document.querySelector('button[name="checkout"]');
            if (submitButton) {
                submitButton.click();
            }

            this.Close();
        } else if (
            event.target.classList.contains('terms-decline') ||
            !this.main.contains(event.target)
        ) {
            this.Close();
        } else if (event.target.classList.contains('expanded-close')) {
            this.CloseText();
        }
    }

    FullTermsText() {
        if (this.main.hasAttribute('data-terms-page')) {
            const pageUrl = this.main.getAttribute('data-terms-page');
            const pageTitle = this.main.getAttribute('data-terms-page-title');

            if (pageUrl) {
                const termsTextLink = this.main.querySelector(
                    `.terms__terms-text a[title='${ pageTitle }']`
                );
                if (termsTextLink) {
                    termsTextLink.addEventListener('click', (event) => {
                        event.preventDefault();

                        if (this.pageContent.classList.contains('loaded')) {
                            this.OpenText();
                        } else {
                            this.FetchFullTermsText(pageUrl);
                        }
                    });
                }
            }
        }
    }

    async FetchFullTermsText(pageUrl) {

        try {
            // Get page as JSON to get body_html, to inject in modal/ drawer
            const pageJson = await (
                await fetch(pageUrl + '.json')
                    .then(res => res.json()));

            const fragment = document.createDocumentFragment();
            const title = document.createElement("h2");
            const content = document.createElement("div");

            title.innerText = pageJson.page.title;
            content.innerHTML = pageJson.page.body_html

            fragment.appendChild(title);
            fragment.appendChild(content);

            this.pageContent.appendChild(fragment);
            this.pageContent.classList.add('loaded');
            this.OpenText();
            return '';
        } catch (error) {
            console.log('Error: ', error);
            return '';
        }
    }

    OpenText() {
        this.main.classList.add('expanded');
    }
    CloseText() {
        this.main.classList.remove('expanded');
    }

    Open() {
        document.addEventListener('click', this.Click);
        this.main.classList.add('active');
    }

    Close() {
        document.removeEventListener('click', this.Click);
        this.CloseText();
        this.main.classList.remove('active');
    }
}

const termsValidation = (event) => {
    const validate = () => {
        const termsCheck = document.getElementById('termsCheck');
        if (!termsCheck || termsCheck.checked == true) return;
        event.preventDefault();

        const termsModal = document.querySelector('#Terms:not(.init)');
        if (termsModal) {
            new TermsModal({ terms: termsModal, termsCheck: termsCheck });
        } else {
            document.dispatchEvent(new CustomEvent('terms:modal:open'));
        }
    };

    validate();
};


/************************* linked products - Title hover  *************************/

const linkedProductCurrentColorName = document.querySelector('.linked-products [data-linked-product-current-color-name]');
const linkedProductsColorName = document.querySelectorAll('.linked-products [data-linked-product-color-name]');

if (linkedProductCurrentColorName && linkedProductsColorName.length > 1) {
    const currentColorNameText = linkedProductCurrentColorName.hasAttribute('data-linked-product-current-color-name') ? linkedProductCurrentColorName.getAttribute('data-linked-product-current-color-name') : ''

    const changeColorName = (text) => {
        linkedProductCurrentColorName.innerText = text
    }

    linkedProductsColorName.forEach(colorName => {

        const colorNameText = colorName.hasAttribute('data-linked-product-color-name') ? colorName.getAttribute('data-linked-product-color-name') : ''

        colorName.addEventListener('mouseover', () => {
            changeColorName(colorNameText)
        });
        colorName.addEventListener('mouseout', () => {
            changeColorName(currentColorNameText)
        });
    });
}




/**
 * PDP media grid - video intersection observer
 */

const videos = document.querySelectorAll('.product-image-grid native-video');
const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
        if (entry.isIntersecting) {
            entry.target.play();
        } else {
            entry.target.pause();
        }
    });
});
videos.forEach(video => observer.observe(video));


/************************* GTC Trigger *************************/

class GTCTabs extends HTMLElement {

    constructor() {
        super();

        this.addEventListener('click', (event) => {
            this.Click(event)
        })

        this.tabs = this.querySelectorAll('.tab-header-item');
    }

    Click(event) {

        const target = event.target;
        const trigger = target.closest('.tab-header-item');
        const id = trigger.getAttribute('id');

        const contentId = id.replace('TabHeader', 'TabContent');
        const continerId = trigger.getAttribute('data-target')
        const container = document.getElementById(continerId);
        const containerItems = container.querySelectorAll('.tab-content-item');


        this.tabs.forEach(tab => {
            if (tab.id === trigger.id) {
                tab.classList.add('active');
            }
            else {
                tab.classList.remove('active');
            }
        })

        containerItems.forEach(content => {
            if (content.id === contentId) {
                content.classList.add('active');
            }
            else {
                content.classList.remove('active');
            }
        });
    }
}

customElements.define('gtc-tabs', GTCTabs);